// Maps each Data API endpoint a workspace can turn off to the SDK handle methods
// that call it. Single source of truth for the two consumers: the runtime handle
// (resourceHandle.ts), which replaces a disabled method with one that throws, and
// the generated editor types (buildResourceCatalogTypes.ts), which omit it so
// authoring and the page type-check surface the problem before it ships.
//
// Endpoints with no bundled method (upserts, bulk creates, previews) are absent:
// authors reach those through `resource.urls`, where a 404 is the honest answer.

import type { DataApiEndpoint } from '@/types/enums';

import type { SdkResource } from './types';

/** Top-level methods on a resource handle, keyed by the endpoint they call. */
const RESOURCE_METHOD_ENDPOINTS: Record<string, DataApiEndpoint> = {
  list: 'index',
  get: 'show',
  count: 'count',
  search: 'search',
  searchAll: 'search',
  create: 'create',
  update: 'update',
  updateMany: 'update_many',
  delete: 'delete',
  restore: 'restore'
};

/** Methods on the nested `analytics` object, keyed by the endpoint they call. */
const ANALYTICS_METHOD_ENDPOINTS: Record<string, DataApiEndpoint> = {
  kpi: 'analytics_kpi',
  timeSeries: 'analytics_time_series',
  leaderboard: 'analytics_leaderboard',
  distribution: 'analytics_distribution'
};

/** Lets callers tell "some analytics disabled" from "all of them". */
export const ANALYTICS_METHOD_COUNT = Object.keys(ANALYTICS_METHOD_ENDPOINTS).length;

/** Each disabled method paired with the endpoint it would have called. */
export type DisabledMethod = { method: string; endpoint: DataApiEndpoint };

function disabledMethodsOf(
  methodEndpoints: Record<string, DataApiEndpoint>,
  disabledEndpoints: readonly string[]
): DisabledMethod[] {
  const disabled = new Set(disabledEndpoints);
  const methods: DisabledMethod[] = [];

  for (const [method, endpoint] of Object.entries(methodEndpoints)) {
    if (disabled.has(endpoint)) {
      methods.push({ method, endpoint });
    }
  }

  return methods;
}

export function disabledResourceMethods(disabledEndpoints: readonly string[]): DisabledMethod[] {
  return disabledMethodsOf(RESOURCE_METHOD_ENDPOINTS, disabledEndpoints);
}

export function disabledAnalyticsMethods(disabledEndpoints: readonly string[]): DisabledMethod[] {
  return disabledMethodsOf(ANALYTICS_METHOD_ENDPOINTS, disabledEndpoints);
}

export function assertResourceMethodAvailable(
  resource: Pick<SdkResource, 'disabled_data_api_endpoints' | 'name'>,
  method: string
): void {
  const endpoint = RESOURCE_METHOD_ENDPOINTS[method];

  if (endpoint && resource.disabled_data_api_endpoints?.includes(endpoint)) {
    throw new Error(disabledEndpointMessage(resource.name, method, endpoint));
  }
}

export function disabledEndpointMessage(
  resourceName: string,
  method: string,
  endpoint: string
): string {
  return `resource.${method}() is unavailable: the "${endpoint}" Data API endpoint is disabled for ${resourceName}. Enable it under Resource settings → API Endpoints.`;
}
